import React, { useEffect, useRef, useState } from "react";
import { RoleChip, StatusChip, describeDevice, formatChf, formatDate, formatDateTime, formatRelative } from "./adminUi";

const PAYMENT_TITLES = { SUBSCRIBE: "Subscribed", EXTEND: "Renewed", CANCEL: "Access ended" };

// "Paper and live", "Paper", "Live" or "None"
function connectedAccounts(user) {
  const names = ["paper", "live"].filter((mode) => user.tradingAccounts?.[mode]);
  if (!names.length) return "None";
  const text = names.join(" and ");
  return text[0].toUpperCase() + text.slice(1);
}

function timeline(payments, logins) {
  const items = [
    ...payments.map((p) => {
      const fromStripe = (p.note || "").startsWith("stripe:");
      return {
        at: p.createdAt, tone: p.action === "CANCEL" ? "danger" : p.action === "EXTEND" ? "blue" : "accent",
        title: PAYMENT_TITLES[p.action] || p.action,
        detail: [fromStripe ? "Stripe" : `by MTT${p.note ? `: ${p.note}` : ""}`, p.amount != null && formatChf(p.amount, 2),
                 p.paidUntil && `until ${formatDate(p.paidUntil)}`].filter(Boolean).join(" · "),
      };
    }),
    ...logins.map((l) => ({
      at: l.timestamp,
      tone: l.status === "failed" ? "danger" : l.action === "SIGNUP" ? "accent" : "dim",
      title: l.action === "SIGNUP" ? "Signed up" : l.status === "failed" ? `Failed login${l.error ? ` (${l.error.toLowerCase()})` : ""}` : "Logged in",
      detail: [describeDevice(l.userAgent), l.ipAddress].filter(Boolean).join(" · "),
    })),
  ];
  return items.sort((a, b) => new Date(b.at) - new Date(a.at));
}

// The portfolio manager role (backend/middleware/require-portfolio-manager.js):
// the portfolio, the reports and the instrument forms. Admins have that access
// anyway, so their switch is on and can't be changed.
function PortfolioManagerSwitch({ auth, user, onChanged }) {
  const [role, setRole]     = useState(user.role);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => setRole(user.role), [user.role]);

  const isAdmin = role === "admin";
  const change = async (on) => {
    if (on && !window.confirm(
      `Make ${user.name} a portfolio manager? They'll see the portfolio and the reports, and can add, replace and remove instruments.`,
    )) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/users/${user.id}/role`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
        body: JSON.stringify({ role: on ? "portfolio_manager" : "user" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "The change failed.");
      setRole(data.role);
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="adm-toggle">
      <div className="adm-toggle__row">
        <span id="adm-pm-label" className="adm-toggle__label">Portfolio manager</span>
        <label className="adm-switch">
          <input
            type="checkbox" role="switch" aria-labelledby="adm-pm-label" aria-describedby="adm-pm-note"
            checked={isAdmin || role === "portfolio_manager"} disabled={saving || isAdmin}
            onChange={(e) => change(e.target.checked)}
          />
          <span className="adm-switch__track" />
        </label>
      </div>
      <p id="adm-pm-note" className="adm-note">
        {isAdmin
          ? "Admins see the portfolio, the reports and the instrument forms anyway."
          : "Portfolio, reports and adding instruments (also replacing and removing them). Applies from the user's next page load."}
      </p>
      {error && <p className="adm-note adm-text-danger" role="alert">{error}</p>}
    </div>
  );
}

const UserDrawer = ({ auth, user, stripeCustomerUrl, onClose, onChanged }) => {
  const [history, setHistory] = useState(null); // { payments, logins }
  const [error, setError]     = useState(null);
  const [saving, setSaving]   = useState(false);
  const [notice, setNotice]   = useState(null);
  const closeRef = useRef(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const controller = new AbortController();
    const headers = { Authorization: "Bearer " + auth.token };
    setHistory(null);
    setError(null);
    Promise.all([
      fetch(`/api/admin/users/${user.id}/payment-history`, { headers, signal: controller.signal }).then((r) => r.json()),
      fetch(`/api/admin/users/${user.id}/login-history?limit=100`, { headers, signal: controller.signal }).then((r) => r.json()),
    ])
      .then(([p, l]) => setHistory({ payments: p.entries || [], logins: l.entries || [] }))
      .catch((err) => { if (err.name !== "AbortError") setError("Couldn't load the history."); });
    return () => controller.abort();
  }, [auth.token, user.id, notice]);

  const changeSubscription = async (body, confirmText, done) => {
    if (!window.confirm(confirmText)) return;
    setSaving(true);
    setNotice(null);
    try {
      const res = await fetch(`/api/admin/users/${user.id}/subscription`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "The change failed.");
      setNotice({ tone: "success", text: done });
      onChanged();
    } catch (err) {
      setNotice({ tone: "error", text: err.message });
    } finally {
      setSaving(false);
    }
  };

  // A free month for users who don't pay through Stripe. For Stripe customers
  // it would be absorbed by the next renewal, so they're managed in Stripe.
  const addMonth = () => {
    const base = Math.max(Date.now(), user.paidUntil ? Date.parse(user.paidUntil) : 0);
    const until = new Date(base);
    until.setMonth(until.getMonth() + 1);
    changeSubscription(
      { action: user.isPaid ? "EXTEND" : "SUBSCRIBE", paidUntil: until.toISOString(), amount: null, note: "Complimentary month (admin)" },
      `Give ${user.name} free access until ${formatDate(until)}?`,
      `Access granted until ${formatDate(until)}.`,
    );
  };
  const endAccess = () =>
    changeSubscription({ action: "CANCEL", amount: null, note: "Ended by admin" }, `End ${user.name}'s complimentary access now?`, "Access ended.");

  const facts = [
    ["Member since", formatDate(user.createdAt)],
    ["Paid until", formatDate(user.paidUntil)],
    ["Total paid", user.totalPaid ? formatChf(user.totalPaid, 2) : "—"],
    ["Last login", formatRelative(user.lastLoginAt)],
    ["Logins (30 d)", `${user.logins30d}${user.failedLogins30d ? ` · ${user.failedLogins30d} failed` : ""}`],
    ["Automatic trading", user.autoTrading ? `On · ${(user.tradedAccounts || []).join(" and ") || "no"} account${(user.tradedAccounts || []).length > 1 ? "s" : ""}` : "Off"],
    ["Trading accounts", connectedAccounts(user)],
  ];
  const items = history ? timeline(history.payments, history.logins) : [];

  return (
    <div className="adm-drawer-backdrop" onClick={onClose}>
      <aside className="adm-drawer" role="dialog" aria-modal="true" aria-labelledby="adm-drawer-title" onClick={(e) => e.stopPropagation()}>
        <header className="adm-drawer__header">
          <div>
            <h2 id="adm-drawer-title">{user.name}<RoleChip role={user.role} /></h2>
            <p className="adm-user__email">{user.email}</p>
          </div>
          <button ref={closeRef} type="button" className="adm-close" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="adm-drawer__status"><StatusChip status={user.status} /></div>

        <dl className="adm-facts">
          {facts.map(([label, value]) => (
            <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
          ))}
        </dl>

        <div className="adm-actions">
          {user.stripeCustomerId && (
            <a className="adm-btn adm-btn--outline" href={`${stripeCustomerUrl}${user.stripeCustomerId}`} target="_blank" rel="noopener noreferrer">
              {user.paying ? "Manage in Stripe" : "Open in Stripe"}
            </a>
          )}
          {!user.paying && (
            <button type="button" className="adm-btn" onClick={addMonth} disabled={saving}>Add a free month</button>
          )}
          {user.status === "complimentary" && (
            <button type="button" className="adm-btn adm-btn--danger" onClick={endAccess} disabled={saving}>End access</button>
          )}
        </div>
        {user.paying && <p className="adm-note">Pays through Stripe: discounts, pauses and cancellations are done in Stripe.</p>}
        {notice && <p className={`adm-note ${notice.tone === "error" ? "adm-text-danger" : "adm-text-accent"}`} role="status">{notice.text}</p>}

        <h3 className="adm-panel__title">Role</h3>
        <PortfolioManagerSwitch key={user.id} auth={auth} user={user} onChanged={onChanged} />

        <h3 className="adm-panel__title">Timeline</h3>
        {error && <p className="adm-note adm-text-danger">{error}</p>}
        {!error && !history && <p className="adm-note">Loading…</p>}
        {history && items.length === 0 && <p className="adm-note">No payments or logins yet.</p>}
        <ul className="adm-feed">
          {items.slice(0, 60).map((item, i) => (
            <li key={`${item.at}-${i}`} className={`adm-feed__item adm-feed__item--${item.tone}`}>
              <span className="adm-feed__dot" aria-hidden="true" />
              <div className="adm-feed__body">
                <span>{item.title}</span>
                {item.detail && <span className="adm-feed__detail">{item.detail}</span>}
              </div>
              <time dateTime={item.at} title={formatDateTime(item.at)} className="adm-feed__time">{formatRelative(item.at)}</time>
            </li>
          ))}
        </ul>
        {items.length > 60 && <p className="adm-note">Showing the latest 60 of {items.length} events.</p>}
      </aside>
    </div>
  );
};

export default UserDrawer;
